import React, { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  useTheme,  
} from "@mui/material";

import { tokens } from "../../../theme";

import { CheckIcon, ExportIcon } from "../../../components/icons";
import { ViewAssignmentDialog } from "../components/AssignmentDialog";

import { PilotAPIResponse, PilotProps } from "../types/view-pilot";
import apiService from "../../../service/Api";
import { GridPaginationModel } from "@mui/x-data-grid";
import Paginator from "../../../components/common/Paginator";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { AxiosError } from "axios";
import MonthlyAssignments from "../components/MonthlyAssignments";

interface PilotAssignmentProps {
  pilot: PilotProps | null;
}

export interface CallsheetProps {
  id: number;
  assignment: Array<{
    role: string;
    notes: string | null;
  }>;
  crew_member: PilotAPIResponse;
  stream: {
    id: number;
    title: string;
    date: string;
    start: string;
    time: string;
    address_one: string | null;
    address_two: string | null;
    suburb: string | null;
    city: string | null;
  };
}

interface Params {
  page: number;
  page_size: number;
  crew_member: number | undefined;
};

export const PilotAssignments: React.FC<PilotAssignmentProps> = ({ pilot }) => {
  const [pastAssignments, setPastAssignments] = useState<CallsheetProps[]>([]);
  const [pastAssignmentsCount, setPastAssignmentsCount] = useState<number>(0);
  const [upcomingAssignments, setUpcomingAssignments] = useState<CallsheetProps[]>([]);
  const [selectedUpcomingAssignment, setSelectedUpcomingAssignment] = useState<number | null>(null);
  const [selectedPastAssignment, setSelectedPastAssignment] = useState<number | null>(null);
  const [assignment, setAssignment] = useState<CallsheetProps | undefined>(undefined);
  const [openViewAssignmentDialog, setOpenViewAssignmentDialog] = useState<boolean>(false);
  const [dialogSrc, setDialogSrc] = useState<string | null>(null);
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 5,
  });

  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const backgroundColor = colors.primary[800] || "#A9B2C3";
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const fetchPastAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiPage = pagination.page + 1;
      const params: Params = {
        page: apiPage,
        page_size: pagination.pageSize,
        crew_member: pilot?.id
      };

      const response = await apiService.get<{ count: number, results: CallsheetProps[] }>(
        "/api/call-sheets/callsheet/past/", params);

      setPastAssignments(response.data.results);
      setPastAssignmentsCount(response.data.count);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || "Failed to load past assignments. Please try again.";
        setError(errorMessage);
      } else {
        console.log({ error }, 'fetchPastAssignments error');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, pilot?.id]);

  const fetchUpcomingAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get<{ results: CallsheetProps[] }>(
        "/api/call-sheets/callsheet/upcoming/", { crew_member: pilot?.id });

      setUpcomingAssignments(response.data.results);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || "Failed to load upcoming assignments. Please try again.";
        setError(errorMessage);
      } else {
        console.log({ error }, 'fetchUpcomingAssignments error');
      }
    } finally {
      setLoading(false);
    }
  }, [pilot?.id]);

  const handleViewAssignment = () => {
    setOpenViewAssignmentDialog(true);
  };

  const handleCloseViewAssignment = () => {
    setOpenViewAssignmentDialog(false);
  };

  useEffect(() => {
    if (selectedPastAssignment) {
      setAssignment(pastAssignments.find(pa => pa.id === selectedPastAssignment));
    };
  }, [pastAssignments, selectedPastAssignment]);

  useEffect(() => {
    if (selectedUpcomingAssignment) {
      setAssignment(upcomingAssignments.find(pa => pa.id === selectedUpcomingAssignment));
    };
  }, [selectedUpcomingAssignment, upcomingAssignments]);

  useEffect(() => {
    if (pilot) {
      fetchPastAssignments();
      fetchUpcomingAssignments();
    }
  }, [fetchPastAssignments, fetchUpcomingAssignments, pilot]);

  if (!pilot) return null;
  
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2 , boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={'flex'}
              alignItems={'center'}
              sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
              gap={'4px'}>
              <CheckIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upcoming Assignments
              </Typography>
            </Box>

            {loading ? (
                <CircularProgress color={'primary'} size={16} />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : upcomingAssignments?.length === 0 ? (
                <Chip
                  size={"small"}
                  sx={{ borderRadius: 1, textTransform: "uppercase" }}
                  label={<i>No upcoming assignments for {pilot.name} yet!</i>}
                />
              ) : (
                <Box>
                  {upcomingAssignments.slice(0,2).map(pa => {
                    return (
                      <Box
                        display={'flex'}
                        key={pa.id}
                        alignItems={'center'}
                        sx={{ py: 2, borderBottom: '1px solid', borderColor: backgroundColor }}
                        justifyContent={'space-between'}>
                          <Box>
                            <Typography variant={'body2'} sx={{ textTransform: 'uppercase' }}>
                              {format(new Date(pa.stream.date), "dd MMM, yyyy")}
                            </Typography>
                            <Typography
                              variant={'body1'}
                              onClick={() => navigate(`/stream-management/view/${pa.stream.id}`)}
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                "&:hover": {
                                  color: reduxTheme.primary_colors[400]
                                }
                              }}>
                              {pa.stream.title}
                            </Typography>
                            <Box>
                              <Typography variant={'subtitle2'}>{`${pa.stream.address_one} ${pa.stream.address_two}`}</Typography>
                              <Typography variant={'caption'}>{`${pa.stream.suburb ? pa.stream.suburb : 'N/A'}, ${pa.stream.city}`}</Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedUpcomingAssignment(pa.id);
                              setDialogSrc('upcoming');
                              handleViewAssignment();
                            }}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: "none",
                            }}
                          >
                            View Details
                          </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <MonthlyAssignments pilot={pilot} />
      </Grid>

      {/* Past Assignments Table */}
      <Grid size={12}>
        <Card sx={{ borderRadius: 2 , boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={'flex'}
              alignItems={'center'}
              sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
              gap={'4px'}>
              <CheckIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Past Assignments
              </Typography>
            </Box>

            {/* Past Assignments Table Headings */}
            <Grid container spacing={2} sx={{ mb: '4px', bgcolor: backgroundColor, padding: 1, borderRadius: 1 }}>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Event</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Role</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Date & Time</Typography>
              </Grid>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Venue</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Payout</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Actions</Typography>
              </Grid>
            </Grid>

            {loading ? (
              <CircularProgress color={'primary'} size={16} />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : pastAssignments?.length === 0 ? (
                <Chip
                  size={"small"}
                  sx={{ borderRadius: 1, textTransform: "uppercase" }}
                  label={<i>No assignments for {pilot.name} yet!</i>}
                />
              ) : (
                <Box>
                  {(pagination && pastAssignments) && pastAssignments?.map((pa: CallsheetProps, index: number) => {
                    const rowBg = index % 2 !== 0 ? backgroundColor : 'transparent';
                    const payout = Math.floor(Math.random() * (5 - 1 + 1)) + 1;
                    return (
                      <Grid
                        key={pa.id}
                        container
                        spacing={2}
                        sx={{
                          my: 1,
                          bgcolor: rowBg,
                          padding: 1,
                          borderRadius: 1
                        }}>
                          <Grid size={3}>
                            <Typography
                              variant={'subtitle2'}
                              onClick={() => navigate(`/stream-management/view/${pa.stream.id}`)}
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                "&:hover": {
                                  color: reduxTheme.primary_colors[400]
                                }
                              }}>
                              {pa.stream.title}
                            </Typography>
                          </Grid>
                          <Grid size={1.5}>
                            <Typography variant={'subtitle2'}>
                              {pa.assignment[0]?.role ? pa.assignment[0]?.role : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={1.5}>
                            <Box>
                              <Typography variant={'subtitle2'}>{format(new Date(pa.stream.date), "dd MMM yyyy")}</Typography>
                              <Typography variant={'subtitle2'}>{pa.stream.start}</Typography>
                            </Box>
                          </Grid>
                          <Grid size={3}>
                            <Typography variant={'subtitle2'}>{`${pa.stream.address_one} ${pa.stream.address_two}`}</Typography>
                            <Typography variant={'caption'}>{`${pa.stream.suburb ? pa.stream.suburb : 'N/A'}, ${pa.stream.city}`}</Typography>
                          </Grid>
                          <Grid size={1.5}>
                            <Typography variant={'subtitle1'} sx={{ fontWeight: 600 }}>{`$${payout * 10}`}</Typography>
                          </Grid>
                          <Grid size={1.5}>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedPastAssignment(pa.id);
                                setDialogSrc('past');
                                handleViewAssignment();
                              }}>
                              <ExportIcon color={'disabled'} sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Grid>
                      </Grid>
                    );
                  })}
                </Box>
              )
            }

            <ViewAssignmentDialog
              open={openViewAssignmentDialog}
              onClose={handleCloseViewAssignment}
              src={dialogSrc}
              assignment={assignment}
            />

            {(pagination && pastAssignments) ? (
              <Paginator
                count={pastAssignmentsCount}
                pagination={pagination}
                setPagination={setPagination}
              />
            ) : null}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
